import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { ContactHero, ContactChannels, ContactForm, ContactOffice, ContactJoin } from '../components/Contact.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  contactHeroKicker: 'Contact',
  contactHeroTitle: "We'd love to hear from you.",
  contactHeroLede: "Old-fashioned actual humans read every message – usually on the same day. Pick the right inbox below, or drop a note in the form and we'll make sure it gets to the right person.",
  contactHeroIllustration: 'postcards',

  contactChannelsKicker: 'Who to talk to',
  contactChannelsTitle: 'Four inboxes, no call centre.',
  contactChannelsLede: "We're a small, personal operation. Pick the address that fits and you'll be talking to the person who actually does the work.",

  contactCh1Label: 'Learner support',
  contactCh1Title: 'Stuck mid-course? Billing question?',
  contactCh1Body: "Deborah and the tutor team answer queries about lessons, payments, your account, the app – anything that's getting in the way of you speaking.",
  contactCh1Email: 'hello@saysomethingin.com',
  contactCh1Sla: 'Replies within two working days, Mon–Fri.',

  contactCh2Label: 'Partnerships & schools',
  contactCh2Title: 'Bring SSi to your school, team, or organisation.',
  contactCh2Body: "Tom, Nick and Aran handle work with schools, TV productions, sports teams, and language bodies. If it's a project, this is the door.",
  contactCh2Email: 'projects@saysomethingin.com',
  contactCh2Sla: 'Replies within two working days, Mon-Fri.',

  contactCh3Label: 'Press & media',
  contactCh3Title: 'Interviews, podcasts, research requests.',
  contactCh3Body: "Catrin handles our communications – press quotes, photos, interview requests, and anything about the company's history and mission.",
  contactCh3Email: 'press@saysomethingin.com',
  contactCh3Sla: 'Same-day reply where possible.',

  contactCh4Label: 'Share your story',
  contactCh4Title: "Tell us how it's going for you.",
  contactCh4Body: "If the course has changed something for you – a job interview, a chat with a grandparent, a weekend in the valleys – we'd love to hear it. A short video, a few paragraphs, or just a sentence. With your permission we share these to inspire the next wave of learners.",
  contactCh4Email: 'stories@saysomethingin.com',
  contactCh4Sla: "We read every one – and we'll always confirm before publishing anything.",

  contactFormKicker: 'Or use the form',
  contactFormTitle: 'Not sure where to start? Write to us here.',
  contactFormLede: "Tell us what's going on in as much or as little detail as you like. We'll send it to the right person and they'll be in touch from their own address (we don't have any no-reply bots here!). ",
  contactFormPoint1: 'Pick a topic so we know which team should get it.',
  contactFormPoint2: "Let us know what's on your mind.",
  contactFormPoint3: "You'll hear back from an analogue, flesh and blood person, usually within two working days.",

  contactFieldName: 'Your name',
  contactFieldNamePh: 'Ruth Jones',
  contactFieldEmail: 'Email',
  contactFieldEmailPh: 'you@example.com',
  contactFieldTopic: "What's this about?",
  contactTopic1: 'Learning / my account',
  contactTopic2: 'Partnerships & schools',
  contactTopic3: 'Press & media',
  contactTopic4: 'Share my learning story',
  contactTopic5: 'Something else',
  contactFieldMessage: 'Message',
  contactFieldMessagePh: 'What would you like to chat to us about?',
  contactFine: 'We only use your details to reply to this enquiry. ',
  contactSubmit: 'Send message →',
  contactSubmitting: 'Sending…',
  contactError: 'Something went wrong — please try again or email us directly.',

  contactSentKicker: 'Sent',
  contactSentTitle: 'Thanks – your message is on its way.',
  contactSentBody: "Thank you for getting in touch with us. We appreciate your interest in what we're trying to do, and we'll get back to you as promptly as possible.",
  contactSentAgain: 'Send another',

  contactOfficeKicker: 'Where we are',
  contactOfficeTitle: "Registered office in Bangor, HQ looking over Caernarfon, and then the world's our oyster.",
  contactOfficeAddrHead: 'Registered address',
  contactOfficeAddrLine1: 'SaySomethingIn Cyf.',
  contactOfficeAddrLine2: 'Glaslyn, Ffordd y Parc',
  contactOfficeAddrLine3: 'Bangor, Gwynedd LL57 4FE',
  contactOfficeAddrLine4: 'Wales, UK',
  contactOfficeHoursHead: 'Office hours',
  contactOfficeHoursLine1: 'Mon–Fri, 9am–5pm GMT',
  contactOfficeHoursLine2: 'Closed on UK bank holidays',
  contactOfficeHoursNote: "The team is spread across Wales, Scotland, England, the Basque Country and occasionally Finland – there's nearly always someone around.",
  contactOfficeLangHead: 'In Welsh or English',
  contactOfficeLangLine1: 'Croeso i chi ysgrifennu atom yn y Gymraeg.',
  contactOfficeLangLine2: 'Write to us in Welsh or English – both are equally welcome.',

  contactJoinTitle: 'Rather just try it?',
  contactJoinBody: "If you're not sure what to ask, three minutes of the method will probably tell you more than any email exchange.",
  contactJoinCta1: 'Try a free sample',
  contactJoinCta2: 'Meet the team',

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero', keys: ['contactHeroKicker','contactHeroTitle','contactHeroLede','contactHeroIllustration'], long:['contactHeroLede'], options:{ contactHeroIllustration:['postcards','bubbles','letter','switchboard'] } },
  { name: 'Channels – intro', keys: ['contactChannelsKicker','contactChannelsTitle','contactChannelsLede'], long:['contactChannelsLede'] },
  { name: 'Channel 1 – Support', keys: ['contactCh1Label','contactCh1Title','contactCh1Body','contactCh1Email','contactCh1Sla'], long:['contactCh1Body'] },
  { name: 'Channel 2 – Partnerships', keys: ['contactCh2Label','contactCh2Title','contactCh2Body','contactCh2Email','contactCh2Sla'], long:['contactCh2Body'] },
  { name: 'Channel 3 – Press', keys: ['contactCh3Label','contactCh3Title','contactCh3Body','contactCh3Email','contactCh3Sla'], long:['contactCh3Body'] },
  { name: 'Channel 4 – Share your story', keys: ['contactCh4Label','contactCh4Title','contactCh4Body','contactCh4Email','contactCh4Sla'], long:['contactCh4Body'] },
  { name: 'Form – left column', keys: ['contactFormKicker','contactFormTitle','contactFormLede','contactFormPoint1','contactFormPoint2','contactFormPoint3'], long:['contactFormLede','contactFormPoint1','contactFormPoint2','contactFormPoint3'] },
  { name: 'Form – fields', keys: ['contactFieldName','contactFieldNamePh','contactFieldEmail','contactFieldEmailPh','contactFieldTopic','contactTopic1','contactTopic2','contactTopic3','contactTopic4','contactFieldMessage','contactFieldMessagePh','contactFine','contactSubmit','contactSubmitting','contactError'], long:['contactFieldMessagePh','contactFine','contactError'] },
  { name: 'Form – sent state', keys: ['contactSentKicker','contactSentTitle','contactSentBody','contactSentAgain'], long:['contactSentBody'] },
  { name: 'Office', keys: ['contactOfficeKicker','contactOfficeTitle','contactOfficeAddrHead','contactOfficeAddrLine1','contactOfficeAddrLine2','contactOfficeAddrLine3','contactOfficeAddrLine4','contactOfficeHoursHead','contactOfficeHoursLine1','contactOfficeHoursLine2','contactOfficeHoursNote','contactOfficeLangHead','contactOfficeLangLine1','contactOfficeLangLine2'], long:['contactOfficeTitle','contactOfficeHoursNote'] },
  { name: 'Join CTA', keys: ['contactJoinTitle','contactJoinBody','contactJoinCta1','contactJoinCta2'], long:['contactJoinBody'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

export default function Contact() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Contact">
      <Header t={tweaks}/>
      <main>
        <ContactHero t={tweaks}/>
        <ContactChannels t={tweaks}/>
        <ContactForm t={tweaks}/>
        <ContactOffice t={tweaks}/>
        <ContactJoin t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
